import { useState, useCallback, useEffect } from "react";
import { Plus, Trash2, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { SearchableSelect } from "../ui/searchable-select";
import { useBillingCategories } from "../../hooks/useBillingCategories";

/**
 * Misc Section - handles miscellaneous line items
 * Includes table display and add/edit dialog
 * Unit Price mode supports allocation to Labor or Material subtotals
 * Unit is auto-populated when selecting from saved items, or defaults to "each"
 */
export const MiscSection = ({
  miscItems,
  miscItemsFromSettings = [],
  miscUnits = [],
  canManageJobs,
  formatCurrency,
  onAddItem,
  onEditItem,
  onDeleteItem,
  miscSubtotal,
  preSelectItem,
  onPreSelectHandled,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedSettingsItem, setSelectedSettingsItem] = useState("");
  
  const [form, setForm] = useState({
    description: "",
    quantity: "1",
    unit: "each",
    cost_type: "unit_price",
    unit_price: "",
    allocate_to: "", // "labor" or "material" for unit_price mode
    material_cost: "",
    labor_cost: "",
    mortar_cost: "",
    notes: "",
    billing_category_override: "",
    source_misc_id: "",
  });
  const billing = useBillingCategories();
  const requiresBilling = !!form.source_misc_id && billing.perJobMisc?.has(form.source_misc_id);

  const resetForm = useCallback(() => {
    setForm({
      description: "",
      quantity: "1",
      unit: "each",
      cost_type: "unit_price",
      unit_price: "",
      allocate_to: "",
      material_cost: "",
      labor_cost: "",
      mortar_cost: "",
      notes: "",
    });
    setEditingItem(null);
    setSelectedSettingsItem("");
  }, []);

  // Handle selecting a misc item from settings
  const handleSelectFromSettings = useCallback((itemId) => {
    setSelectedSettingsItem(itemId);
    if (!itemId) return;
    
    const settingsItem = miscItemsFromSettings.find(m => m.id === itemId);
    if (settingsItem) {
      // Determine cost type based on what values are set
      const hasUnitPrice = settingsItem.unit_price > 0;
      const hasMaterialOrLabor = (settingsItem.material_cost > 0) || (settingsItem.labor_cost > 0);
      const costType = hasMaterialOrLabor ? "material_labor" : "unit_price";
      
      setForm({
        description: settingsItem.description || "",
        quantity: settingsItem.quantity?.toString() || "1",
        unit: settingsItem.unit || "each",
        cost_type: costType,
        unit_price: hasUnitPrice ? settingsItem.unit_price.toFixed(2) : "",
        allocate_to: "",
        material_cost: settingsItem.material_cost ? settingsItem.material_cost.toFixed(2) : "",
        labor_cost: settingsItem.labor_cost ? settingsItem.labor_cost.toFixed(2) : "",
        mortar_cost: "",
        notes: settingsItem.notes || "",
        billing_category_override: "",
        source_misc_id: settingsItem.id,
      });
    }
  }, [miscItemsFromSettings]);

  const openDialog = useCallback((item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        description: item.description || "",
        quantity: item.quantity?.toString() || "1",
        unit: item.unit || "",
        cost_type: item.cost_type || "unit_price",
        unit_price: item.unit_price?.toFixed(2) || "",
        allocate_to: item.allocate_to || "",
        material_cost: item.material_cost ? (item.material_cost / item.quantity).toFixed(2) : "",
        labor_cost: item.labor_cost ? (item.labor_cost / item.quantity).toFixed(2) : "",
        mortar_cost: item.mortar_cost ? (item.mortar_cost / item.quantity).toFixed(2) : "",
        notes: item.notes || "",
        billing_category_override: item.billing_category_override || "",
        source_misc_id: item.source_misc_id || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  }, [resetForm]);

  // Auto-open dialog when preSelectItem is set from universal search
  useEffect(() => {
    if (preSelectItem) {
      const miscItem = miscItemsFromSettings.find(m => m.id === preSelectItem.id);
      if (miscItem) {
        setEditingItem(null);
        setSelectedSettingsItem(miscItem.id);
        const hasUnitPrice = miscItem.unit_price > 0;
        const hasMaterialOrLabor = (miscItem.material_cost > 0) || (miscItem.labor_cost > 0);
        const costType = hasMaterialOrLabor ? "material_labor" : "unit_price";
        setForm({
          description: miscItem.description || "",
          quantity: miscItem.quantity?.toString() || "1",
          unit: miscItem.unit || "each",
          cost_type: costType,
          unit_price: hasUnitPrice ? miscItem.unit_price.toFixed(2) : "",
          allocate_to: "",
          material_cost: miscItem.material_cost ? miscItem.material_cost.toFixed(2) : "",
          labor_cost: miscItem.labor_cost ? miscItem.labor_cost.toFixed(2) : "",
          mortar_cost: "",
          notes: miscItem.notes || "",
          billing_category_override: "",
          source_misc_id: miscItem.id,
        });
        setDialogOpen(true);
      }
      if (onPreSelectHandled) onPreSelectHandled();
    }
  }, [preSelectItem, miscItemsFromSettings, onPreSelectHandled]);

  const handleSubmit = useCallback(() => {
    const quantity = parseFloat(form.quantity) || 1;
    let total = 0;
    let materialCost = 0;
    let laborCost = 0;
    let mortarCost = 0;

    if (form.cost_type === "unit_price") {
      const unitPrice = parseFloat(form.unit_price) || 0;
      const lineTotal = quantity * unitPrice;
      
      // Allocate based on checkbox selection
      if (form.allocate_to === "labor") {
        laborCost = lineTotal;
      } else if (form.allocate_to === "material") {
        materialCost = lineTotal;
      } else {
        // Default: goes to misc subtotal (total field)
        total = lineTotal;
      }
    } else {
      materialCost = (parseFloat(form.material_cost) || 0) * quantity;
      laborCost = (parseFloat(form.labor_cost) || 0) * quantity;
      mortarCost = (parseFloat(form.mortar_cost) || 0) * quantity;
    }

    const newItem = {
      id: editingItem?.id || crypto.randomUUID(),
      description: form.description,
      quantity,
      unit: form.unit || "each",
      cost_type: form.cost_type,
      unit_price: form.cost_type === "unit_price" ? (parseFloat(form.unit_price) || 0) : 0,
      allocate_to: form.cost_type === "unit_price" ? form.allocate_to : "",
      total,
      material_cost: materialCost,
      labor_cost: laborCost,
      mortar_cost: mortarCost,
      notes: form.notes,
      billing_category_override: form.billing_category_override || null,
      source_misc_id: form.source_misc_id || null,
    };

    if (editingItem) {
      onEditItem(newItem);
    } else {
      onAddItem(newItem);
    }

    setDialogOpen(false);
    resetForm();
  }, [form, editingItem, onAddItem, onEditItem, resetForm]);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl flex items-center gap-3 text-slate-700">
            <span>Misc</span>
            <span className="text-muted-foreground font-normal">&ndash;</span>
            <span className="text-xl font-normal text-slate-500">Subtotal</span>
            <span className="font-mono font-bold text-2xl text-slate-700" data-testid="misc-subtotal">{formatCurrency(miscSubtotal)}</span>
          </CardTitle>
          {canManageJobs && (
            <Button size="lg" className="text-lg px-6" onClick={() => openDialog()} data-testid="add-misc-item-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {(!miscItems || miscItems.length === 0) ? (
            <div className="p-8 text-center text-muted-foreground">
              <p>No misc items added yet</p>
              <p className="text-sm mt-1">Click &quot;Add Item&quot; for items not in predefined lists</p>
            </div>
          ) : (
            <div>
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="bg-slate-100 border-b-2 border-slate-200">
                    <TableHead className="h-8 py-1 text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Description</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Qty</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Unit</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Unit Price</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Material</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Mortar/Other</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Total</TableHead>
                    {canManageJobs && <TableHead className="h-8 py-1 w-20"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {miscItems.map((item, idx) => (
                    <TableRow key={item.id} className="border-b border-slate-100">
                      <TableCell className="py-3">
                        <div>
                          <span className="font-medium">{item.description}</span>
                          {item.cost_type === "material_labor" && (
                            <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Mat+Labor</span>
                          )}
                          {item.cost_type === "unit_price" && item.allocate_to === "labor" && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">→ Labor</span>
                          )}
                          {item.cost_type === "unit_price" && item.allocate_to === "material" && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">→ Material</span>
                          )}
                          {item.billing_category_override && <span className="ml-2 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid={`bid-misc-billing-override-${item.id}`}>Billing: {item.billing_category_override}</span>}
                          {item.notes && <p className="text-xs text-muted-foreground italic">{item.notes}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">{item.unit || "-"}</TableCell>
                      <TableCell className="text-right font-mono">{item.unit_price > 0 ? formatCurrency(item.unit_price) : "-"}</TableCell>
                      <TableCell className="text-right font-mono">{item.material_cost > 0 ? formatCurrency(item.material_cost) : "-"}</TableCell>
                      <TableCell className="text-right font-mono">{item.mortar_cost > 0 ? formatCurrency(item.mortar_cost) : "-"}</TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {(() => {
                          const itemTotal = item.total || item.amount || ((item.unit_price || 0) * (item.quantity || 1));
                          return itemTotal > 0 ? formatCurrency(itemTotal) : "-";
                        })()}
                      </TableCell>
                      {canManageJobs && (
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(item)} data-testid={`edit-bid-misc-${item.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDeleteItem(item.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Misc Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit" : "Add"} Misc Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Select from Settings dropdown - only show when adding new item */}
            {!editingItem && miscItemsFromSettings.length > 0 && (
              <div className="form-group">
                <Label>Select from Saved Items</Label>
                <Select value={selectedSettingsItem} onValueChange={handleSelectFromSettings}>
                  <SelectTrigger data-testid="misc-settings-select">
                    <SelectValue placeholder="Choose a saved misc item..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...miscItemsFromSettings].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.description}
                        {item.material_cost > 0 || item.labor_cost > 0 
                          ? ` (Mat: $${item.material_cost}, Lab: $${item.labor_cost})`
                          : item.unit_price > 0 
                            ? ` ($${item.unit_price}/ea)`
                            : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Or enter custom item details below</p>
              </div>
            )}

            <div className="form-group">
              <Label>Description *</Label>
              <Input 
                value={form.description} 
                onChange={(e) => setForm(f => ({...f, description: e.target.value}))} 
                placeholder="Item description"
                data-testid="misc-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Quantity *</Label>
                <NumericInput 
                  decimals={2}
                  min={0}
                  value={form.quantity} 
                  onChange={(e) => setForm(f => ({...f, quantity: e.target.value}))} 
                  placeholder="1"
                  data-testid="misc-quantity-input"
                />
              </div>
              <div className="form-group">
                <Label>Unit</Label>
                {miscUnits.length > 0 ? (
                  <Select 
                    value={form.unit || "each"} 
                    onValueChange={(v) => setForm(f => ({...f, unit: v}))}
                  >
                    <SelectTrigger data-testid="misc-unit-select">
                      <SelectValue placeholder="Select unit..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...miscUnits].sort((a, b) => a.localeCompare(b)).map((unit) => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input 
                    value={form.unit || "each"}
                    onChange={(e) => setForm(f => ({...f, unit: e.target.value}))} 
                    placeholder="each"
                    data-testid="misc-unit-input"
                  />
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {miscUnits.length === 0 ? "Add units in Settings → Options" : "e.g., each, day, week, lump sum"}
                </p>
              </div>
            </div>
            <div className="form-group">
              <Label>Cost Type *</Label>
              <Select value={form.cost_type} onValueChange={(v) => setForm(f => ({...f, cost_type: v, unit_price: "", material_cost: "", labor_cost: "", mortar_cost: ""}))}>
                <SelectTrigger data-testid="misc-cost-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit_price">Unit Price</SelectItem>
                  <SelectItem value="material_labor">Material &amp; Labor Costs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.cost_type === "unit_price" ? (
              <>
                <div className="form-group">
                  <Label>Unit Price ($) *</Label>
                  <NumericInput 
                    decimals={2}
                    min={0}
                    value={form.unit_price} 
                    onChange={(e) => setForm(f => ({...f, unit_price: e.target.value}))} 
                    placeholder="0.00"
                    data-testid="misc-unit-price-input"
                  />
                </div>
                {/* Allocation checkboxes - only for unit_price mode */}
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                  <Label className="text-sm font-medium">Allocate to (optional)</Label>
                  <div className="flex gap-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="allocate-labor"
                        checked={form.allocate_to === "labor"}
                        onCheckedChange={(checked) => setForm(f => ({
                          ...f, 
                          allocate_to: checked ? "labor" : (f.allocate_to === "labor" ? "" : f.allocate_to)
                        }))}
                        data-testid="misc-allocate-labor-checkbox"
                      />
                      <Label htmlFor="allocate-labor" className="text-sm cursor-pointer">
                        Labor Subtotal
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="allocate-material"
                        checked={form.allocate_to === "material"}
                        onCheckedChange={(checked) => setForm(f => ({
                          ...f, 
                          allocate_to: checked ? "material" : (f.allocate_to === "material" ? "" : f.allocate_to)
                        }))}
                        data-testid="misc-allocate-material-checkbox"
                      />
                      <Label htmlFor="allocate-material" className="text-sm cursor-pointer">
                        Material Subtotal
                      </Label>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    If neither is selected, amount goes to Misc Subtotal
                  </p>
                </div>
                {form.quantity && form.unit_price && (
                  <div className={`p-3 rounded-lg ${
                    form.allocate_to === "labor" ? "bg-blue-50" : 
                    form.allocate_to === "material" ? "bg-green-50" : "bg-gray-50"
                  }`}>
                    <div className="flex justify-between items-center">
                      <span className={`text-sm font-medium ${
                        form.allocate_to === "labor" ? "text-blue-800" : 
                        form.allocate_to === "material" ? "text-green-800" : "text-gray-800"
                      }`}>Line Total:</span>
                      <span className={`text-lg font-bold ${
                        form.allocate_to === "labor" ? "text-blue-700" : 
                        form.allocate_to === "material" ? "text-green-700" : "text-gray-700"
                      }`}>
                        {formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.unit_price) || 0))}
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${
                      form.allocate_to === "labor" ? "text-blue-600" : 
                      form.allocate_to === "material" ? "text-green-600" : "text-gray-600"
                    }`}>
                      → Goes to {form.allocate_to === "labor" ? "Labor" : form.allocate_to === "material" ? "Materials" : "Misc"} Subtotal
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="form-group">
                    <Label>Material Cost per Unit ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.material_cost} 
                      onChange={(e) => setForm(f => ({...f, material_cost: e.target.value}))} 
                      placeholder="0.00"
                      data-testid="misc-material-cost-input"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Labor Cost per Unit ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.labor_cost} 
                      onChange={(e) => setForm(f => ({...f, labor_cost: e.target.value}))} 
                      placeholder="0.00"
                      data-testid="misc-labor-cost-input"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Mortar/Other per Unit ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.mortar_cost} 
                      onChange={(e) => setForm(f => ({...f, mortar_cost: e.target.value}))} 
                      placeholder="0.00"
                      data-testid="misc-mortar-cost-input"
                    />
                  </div>
                </div>
                {form.quantity && (form.material_cost || form.labor_cost || form.mortar_cost) && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-1">
                    {form.material_cost && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-800">Material Total:</span>
                        <span className="font-bold text-green-700">{formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.material_cost) || 0))} → Materials</span>
                      </div>
                    )}
                    {form.labor_cost && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-800">Labor Total:</span>
                        <span className="font-bold text-green-700">{formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.labor_cost) || 0))} → Labor</span>
                      </div>
                    )}
                    {form.mortar_cost && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-800">Mortar/Other Total:</span>
                        <span className="font-bold text-green-700">{formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.mortar_cost) || 0))} → Materials</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <div className={`form-group ${requiresBilling && !form.billing_category_override ? "rounded-md border border-amber-300 bg-amber-50 p-2" : ""}`}>
              <Label>{requiresBilling ? "Billing Category (required for this item)" : "Billing Category Override"}</Label>
              <SearchableSelect
                value={form.billing_category_override}
                onValueChange={(v) => setForm(f => ({ ...f, billing_category_override: v || "" }))}
                options={billing.categories.map((c) => ({ value: c, label: c }))}
                placeholder={requiresBilling ? "Choose where this line bills on the SOV..." : billing.misc[form.source_misc_id] ? `Default: ${billing.misc[form.source_misc_id]} (from misc item)` : "Use misc item's billing category"}
                searchPlaceholder="Search billing categories..."
                data-testid="bid-misc-billing-override-select"
              />
              <p className="text-xs text-muted-foreground mt-1">{requiresBilling ? "This item is set to pick its billing category per job — pick one to continue." : "Only for this line on this bid sheet — changes where it lands on the Schedule of Values."}</p>
            </div>
            <div className="form-group">
              <Label>Notes</Label>
              <Textarea 
                value={form.notes} 
                onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} 
                rows={2} 
                placeholder="Optional notes..." 
              />
            </div>
            <Button 
              onClick={handleSubmit} 
              className="w-full" 
              disabled={!form.description || !form.quantity || (requiresBilling && !form.billing_category_override) || (form.cost_type === "unit_price" ? !form.unit_price : (!form.material_cost && !form.labor_cost && !form.mortar_cost))} 
              data-testid="add-misc-submit-btn"
            >
              {editingItem ? "Save Changes" : "Add Misc Item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MiscSection;
